import {Injectable, OnDestroy} from '@angular/core';
import {BehaviorSubject, Observable, of, Subject, Subscription, throwError} from 'rxjs';
import {Block, BlockType, BucketType, Token, TokenInstance} from '@paperlessio/sdk/api/models';
import {TokenService} from './token.service';
import {ToastService} from '@paperlessio/sdk/api/util';
import {catchError, debounceTime, first, map, mergeMap, tap} from 'rxjs/operators';
import {BucketChannelService} from '@management/base/services/bucket-channel/bucket-channel.service';
import {BlockStore} from '@blocks/block/block.store';
import {CalculatedTokenValueStore} from '@shared/rex/calculated-token-value.store';
import {BaseStore} from '@paperlessio/sdk/api/stores';

@Injectable({providedIn: 'root'})
export class TokenStore extends BaseStore<Token, TokenService> implements OnDestroy {
  public tokens: BehaviorSubject<Token[]> = new BehaviorSubject<Token[]>([]);
  public changedToken: Subject<Token> = new Subject<Token>();
  public debouncedTokenUpdate: Subject<Token> = new Subject<Token>();

  public listenToBlockChanges = new BehaviorSubject<boolean>(false);
  public tokenInstances = new Map<string, TokenInstance[]>();

  public bucket_type: BucketType;
  public bucket_id: number;

  private subs = new Subscription();
  private tokenInstancesSub = new Subscription();
  private debounceTime = 50;

  constructor(
    public service: TokenService,
    public toast: ToastService,
    private bucketChannelService: BucketChannelService,
    private calculatedTokenValueStore: CalculatedTokenValueStore,
    private blockStore: BlockStore
  ) {
    super(service, toast);

    this.subs.add(
      this.bucketChannelService.tokensChangedSubject?.subscribe(tokens => {
        tokens?.forEach((token: Token) => {
          this.getTokenByName(token.name)
            .pipe(
              first(),
              map(oldToken =>
                token.value !== undefined && oldToken.value !== undefined &&
                token.value !== oldToken.value ? token : null))
            .subscribe(newToken => {
              if (newToken) {
                this.changedToken.next(newToken);
              }
            });
        });

        this.tokens.next(tokens);
      })
    );

    this.subs.add(this.listenToBlockChanges.subscribe((val: boolean) => {
      if (val) {
        this.tokenInstancesSub.add(this.updateTokenInstances().subscribe());
      } else {
        this.tokenInstancesSub.unsubscribe();
      }
    }));

    this.subs.add(this.debouncedTokenUpdate
      .pipe(debounceTime(this.debounceTime))
      .subscribe(() => {
        this.updateTokenInstances().pipe(first()).subscribe();
      }));

    this.subs.add(this.tokens.subscribe(tokens => {
      this.calculatedTokenValueStore.loadConstantTokenValues(tokens);
    }));
  }

  public initByBucket(bucket_type: BucketType, bucket_id: number, forceLoad: boolean = false): Observable<Token[]> {
    // TODO: Temporary fix!
    // The whole Token(Mutation) Universe lives in the Submittable world, not using its subclasses
    // Document and FormVersion!
    if (bucket_type === BucketType.FormVersion) {
      bucket_type = BucketType.Submittable;
    }

    if (!forceLoad && bucket_type === this.bucket_type && bucket_id === this.bucket_id) {
      return of(this.tokens.value);
    }

    this.bucket_id = bucket_id;
    this.bucket_type = bucket_type;

    return this.service.allFromBucket(bucket_type, bucket_id).pipe(
      tap(tokens => {
        this.calculatedTokenValueStore.loadConstantTokenValues(tokens);
        this.tokens.next(tokens);
      })
    );
  }

  public getTokenByName(name: string): Observable<Token> {
    return this.tokens.pipe(
      map(tokens => tokens?.find(t => t.name === name))
    );
  }

  get(id: number, returnExisting = true, params = {}): Observable<Token> {
    if (returnExisting && !!this.tokens.value) {
      const found = this.tokens.value.find(x => x.id === id);
      if (!!found) {
        return of(found);
      }
    }
    return super.get(id, returnExisting, params);
  }

  public create(
    data: Token,
    params: {} = {},
    nextCurrent: boolean = false,
    fetchAll: boolean = true): Observable<Token> {
    // TODO: Temporary fix!
    // The whole Token Universe lives in the Submittable world, not using its subclasses
    // Document and FormVersion!
    if (data.bucket_type === BucketType.FormVersion) {
      data.bucket_type = BucketType.Submittable;
    }

    return super.create(data, params, nextCurrent, fetchAll).pipe(
      tap(created => {
        this.tokens.next([...this.tokens.value, created]);
      })
    );
  }

  public update(data: Token, update_subjects: boolean = true): Observable<Token> {
    this.changedToken.next(data);
    return super.update(data, update_subjects).pipe(
      tap(updated => {
        if (update_subjects) {
          const idx = this.tokens.value?.findIndex(x => x.id === updated.id);
          this.tokens.value[idx] = updated;
          this.tokens.next(this.tokens.value);
        }
      })
    );
  }

  public delete(id?: number): Observable<Token> {
    return this.service.delete(id).pipe(
      tap(() => {
        const idx = this.tokens.value?.findIndex(x => x.id === id);
        this.tokens.value?.splice(idx, 1);
        this.tokens.next(this.tokens.value);
      }),
      catchError(error => {
        this.handleDeleteError(error);
        return throwError(error);
      }));
  }

  /**
   * Get Sorted tokens by topologicalBlockPosition and offset in the component
   * IMPORTANT: strips tokens that dont't occure in the document
   */
  public getSortedTokens(): Observable<Token[]> {
    return this.tokens.pipe(
      map((tokens: Token[]) => {
        return tokens.filter(t => t.occurrences).sort((a: Token, b: Token) => {
          return this.compareTokenInstancePosition(
            this.tokenInstances.get(a.name)[0],
            this.tokenInstances.get(b.name)[0]
          );
        });
      }));
  }

  /**
   * Returns a map of TokenInstances sorted by page
   * @param tokenName name of the token that has the instances
   */
  public getTokenInstancesByPage(tokenName: string): Map<number, TokenInstance[]> {
    const sortedInstances = new Map<number, TokenInstance[]>();

    this.tokenInstances.get(tokenName).forEach(i => {
      const pageId = this.blockStore.getParentPageId(i.blockId);
      const pageInstances = sortedInstances.get(pageId) ? sortedInstances.get(pageId) : [];
      sortedInstances.set(pageId, [...pageInstances, i]);
    });

    return sortedInstances;
  }

  public updateTokenInstances(): Observable<Token[]> {
    return this.blockStore.blocks.pipe(
      mergeMap(blocks => this.getTokenInstances(blocks)),
      mergeMap(tokenInstances => this.populateTokens(tokenInstances))
    );
  }

  public updateTokenInstancesDebounced(): void {
    this.debouncedTokenUpdate.next(null);
  }

  /**
   * Returns the blocks instances out of the delta of the block settings
   * @param blocks to create the instances from
   */
  private getTokenInstances(blocks: Block[]): Observable<Map<string, TokenInstance[]>> {
    if (!blocks) {
      return of();
    }

    const tokenInstances = new Map<string, TokenInstance[]>();
    let offset = 0;

    this.blockStore.dfsPreOrderTopologicalSort();

    blocks
      .filter(block => block.type === BlockType.RichText)
      .forEach(block => {
        if (!block.settings.delta) {
          return of();
        }

        const ops = block.settings.delta.ops;

        ops.forEach(op => {
          if (typeof op.insert !== 'string' && Object.keys(op.insert)[0] === 'variable') {
            const instances = tokenInstances.get(op.insert.variable) ? tokenInstances.get(op.insert.variable) : [];
            tokenInstances.set(op.insert.variable, [...instances, {
              id: block.id.toString() + offset.toString() + block.topologicalPosition.toString(),
              blockId: block.id,
              offset: offset++,
              topologicalBlockPosition: block.topologicalPosition
            }]);
          }
        });
      });

    return of(tokenInstances);
  }

  /**
   * Populates the tokens with additional information of their attached instances
   * @param tokenInstances all instances available that for populating the tokens
   */
  private populateTokens(tokenInstances: Map<string, TokenInstance[]>): Observable<Token[]> {
    if (!tokenInstances) {
      return of();
    }

    this.tokenInstances = tokenInstances;

    return this.tokens.pipe(
      map(tokens => {
        tokens?.forEach((token: Token) => {
          const instances = this.tokenInstances.get(token.name);
          if (instances?.length) {
            instances.sort(this.compareTokenInstancePosition);
          }
          token.occurrences = instances?.length ?? 0;
        });

        return tokens;
      }));
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private compareTokenInstancePosition = (a: TokenInstance, b: TokenInstance): number => {
    if (a.topologicalBlockPosition === b.topologicalBlockPosition) {
      return (a.offset < b.offset) ? -1 : (a.offset > b.offset) ? 1 : 0;
    } else {
      return (a.topologicalBlockPosition < b.topologicalBlockPosition) ? -1 : 1;
    }
  };
}
